import * as RToast from "@radix-ui/react-toast";
import { AlertTriangle, CheckCircle, Info, X, XCircle, type LucideIcon } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from "react";

import { cn } from "../utils/cn";

export type ToastKind = "success" | "warning" | "error" | "info";

export interface ToastInput {
  kind?: ToastKind;
  title?: ReactNode;
  message: ReactNode;
  /** Milliseconds before auto-dismiss. `null` keeps the toast until dismissed.
   * Defaults: error → null, warning → 8000, with an action → 8000, others → 5000. */
  duration?: number | null;
  /** One button in the toast, e.g. „Rückgängig". Clicking it closes the toast. */
  action?: ToastAction;
}

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem extends ToastInput {
  id: string;
  open: boolean;
}

interface ToastContextValue {
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastKind, LucideIcon> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

const ICON_CLASS: Record<ToastKind, string> = {
  success: "text-[#4ADE80]",
  warning: "text-[#FB923C]",
  error: "text-[#F87171]",
  info: "text-[#60A5FA]",
};

const LONG_DURATION_MS = 8000;
const SHORT_DURATION_MS = 5000;

function defaultDuration(kind: ToastKind, hasAction: boolean): number | null {
  if (kind === "error") return null;
  // 5 s is too short to reach for an action button.
  if (kind === "warning" || hasAction) return LONG_DURATION_MS;
  return SHORT_DURATION_MS;
}

export function ToastProvider({ children }: PropsWithChildren): ReactElement {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((xs) => xs.map((t) => (t.id === id ? { ...t, open: false } : t)));
  }, []);

  const push = useCallback((input: ToastInput): string => {
    const id = Math.random().toString(36).slice(2);
    // A repeated failure shouldn't stack the same toast again (#22): error
    // toasts stay until dismissed, so identical ones would pile up.
    setToasts((xs) =>
      xs.some(
        (t) =>
          t.open && t.kind === input.kind && t.title === input.title && t.message === input.message,
      )
        ? xs
        : [...xs, { ...input, id, open: true }],
    );
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      <RToast.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => {
          const kind: ToastKind = t.kind ?? "info";
          const Icon = ICONS[kind];
          const duration =
            t.duration === undefined ? defaultDuration(kind, Boolean(t.action)) : t.duration;
          return (
            <RToast.Root
              key={t.id}
              open={t.open}
              onOpenChange={(open) => !open && dismiss(t.id)}
              duration={duration ?? 24 * 60 * 60 * 1000}
              className={cn(
                "flex items-start gap-3 rounded-md bg-neutral-900 px-5 py-4 text-neutral-0 shadow-lg",
                "data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
                "data-[state=closed]:animate-out data-[state=closed]:fade-out",
              )}
            >
              <Icon size={18} className={cn("mt-0.5 shrink-0", ICON_CLASS[kind])} />
              <div className="flex-1 text-sm leading-relaxed">
                {t.title && <RToast.Title className="mb-0.5 font-semibold">{t.title}</RToast.Title>}
                <RToast.Description>{t.message}</RToast.Description>
              </div>
              {t.action && (
                <RToast.Action
                  altText={t.action.label}
                  onClick={t.action.onClick}
                  className="shrink-0 cursor-pointer rounded-sm text-sm font-semibold text-neutral-0 underline underline-offset-2 hover:text-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-0"
                >
                  {t.action.label}
                </RToast.Action>
              )}
              <RToast.Close
                aria-label="Benachrichtigung schließen"
                className="inline-flex text-neutral-400 hover:text-neutral-0"
              >
                <X size={16} />
              </RToast.Close>
            </RToast.Root>
          );
        })}
        <RToast.Viewport className="fixed top-20 right-4 left-4 z-70 flex flex-col gap-2.5 outline-none sm:left-auto sm:right-6 sm:max-w-sm" />
      </RToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider>.");
  }
  return ctx;
}
